/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  DataTable,
  Select,
  TextInput,
  toast
} from '@etherisc/ui-kit';
import { RefreshCw, Play, Calendar, TrendingUp, CheckCircle2 } from 'lucide-react';
import { 
  usePremiumEarningCandidates, 
  useProcessBulkPremiumEarning 
} from '../../hooks/insurance/useInsuranceBatch';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { Modal } from '../../components/ledger/Modal';

export const PremiumEarningManagement = () => {
  const [filters, setFilters] = useState({
    organizationId: '',
    currency: '',
    minAmount: '',
    maxAmount: ''
  });
  
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [showBulkProcessModal, setShowBulkProcessModal] = useState(false);
  const [bulkProcessDate, setBulkProcessDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: candidatesData, refetch, isLoading } = usePremiumEarningCandidates(filters);
  const processBulkEarning = useProcessBulkPremiumEarning();

  const candidates = candidatesData?.candidates || [];
  const totalEarnable = candidates
    .filter(c => selectedCandidates.length === 0 || selectedCandidates.includes(c.id))
    .reduce((sum, c) => sum + parseFloat(c.earnableAmount), 0);

  const columns = [
    {
      id: 'select',
      header: ({ table }: { table: any }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedCandidates(candidates.map(c => c.id));
            } else {
              setSelectedCandidates([]);
            }
          }}
          className="rounded border-gray-300"
        />
      ),
      cell: ({ row }: { row: { original: any } }) => (
        <input
          type="checkbox"
          checked={selectedCandidates.includes(row.original.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedCandidates(prev => [...prev, row.original.id]);
            } else {
              setSelectedCandidates(prev => prev.filter(id => id !== row.original.id));
            }
          }}
          className="rounded border-gray-300"
        />
      ),
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      cell: ({ row }: { row: { original: any } }) => (
        <div>
          <div className="font-medium">{row.original.clientName}</div>
          <div className="text-sm text-muted-foreground">{row.original.clientCode}</div>
        </div>
      ),
    },
    {
      accessorKey: 'costCenterName',
      header: 'Policy',
      cell: ({ row }: { row: { original: any } }) => (
        <div>
          <div className="font-medium">{row.original.costCenterName}</div>
          <div className="text-sm text-muted-foreground">{row.original.costCenterCode}</div>
        </div>
      ),
    },
    {
      accessorKey: 'premiumAmount',
      header: 'Premium Amount',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="font-semibold">
          {formatCurrency(row.original.premiumAmount, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'earnedToDate',
      header: 'Earned to Date',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="text-muted-foreground">
          {formatCurrency(row.original.earnedToDate, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'earnableAmount',
      header: 'Earnable Amount',
      cell: ({ row }: { row: { original: any } }) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(row.original.earnableAmount, row.original.currency)}
        </span>
      ),
    },
    {
      accessorKey: 'coverageStartDate',
      header: 'Coverage Start',
      cell: ({ row }: any) => formatDate(row.original.coverageStartDate),
    },
    {
      accessorKey: 'coverageEndDate',
      header: 'Coverage End',
      cell: ({ row }: any) => formatDate(row.original.coverageEndDate),
    },
    {
      accessorKey: 'earnablePercentage',
      header: 'Earnable %',
      cell: ({ row }: { row: { original: any } }) => (
        <div className="flex items-center gap-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full" 
              style={{ width: `${Math.min(100, row.original.earnablePercentage)}%` }}
            />
          </div>
          <span className="text-sm font-medium">
            {row.original.earnablePercentage.toFixed(1)}%
          </span>
        </div>
      ),
    }
  ];

  const handleBulkProcess = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('Please select at least one candidate to process');
      return;
    }

    try {
      await processBulkEarning.mutateAsync({
        candidateIds: selectedCandidates,
        effectiveDate: bulkProcessDate
      });
      
      toast.success(`Successfully processed ${selectedCandidates.length} premium earning entries`);
      setSelectedCandidates([]);
      setShowBulkProcessModal(false);
      refetch();
    } catch (error: any) {
      toast.error(`Failed to process premium earning: ${error.message}`);
    }
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === candidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(candidates.map(c => c.id));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Premium Earning Management
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Manage premium earning for active policies based on coverage periods
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              {selectedCandidates.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => setShowBulkProcessModal(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Process Selected ({selectedCandidates.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Candidates</p>
                    <p className="text-2xl font-bold">{candidates.length}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Selected</p>
                    <p className="text-2xl font-bold text-green-600">{selectedCandidates.length}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Earnable</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(totalEarnable.toString(), 'EUR')}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg. Earning %</p>
                    <p className="text-2xl font-bold">
                      {candidates.length > 0 
                        ? (candidates.reduce((sum, c) => sum + c.earnablePercentage, 0) / candidates.length).toFixed(1)
                        : '0.0'
                      }%
                    </p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 font-bold">%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="col-span-full text-sm font-medium text-gray-700 mb-2">Filter Candidates</h3>
            
            <Select
              label="Currency"
              value={filters.currency || 'none'}
              onValueChange={(value) => setFilters(prev => ({ 
                ...prev, 
                currency: value === 'none' ? '' : value 
              }))}
              options={[
                { value: 'none', label: 'All Currencies' },
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'GBP', label: 'GBP' }
              ]}
            />

            <TextInput
              label="Min Amount"
              value={filters.minAmount}
              onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
              placeholder="0.00"
            />

            <TextInput
              label="Max Amount"
              value={filters.maxAmount}
              onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
              placeholder="1000.00"
            />

            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ organizationId: '', currency: '', minAmount: '', maxAmount: '' })}
              >
                Clear Filters
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedCandidates.length === candidates.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>

          {/* Candidates Table */}
          <DataTable
            data={candidates}
            columns={columns}
            loading={isLoading}
          />

          {candidates.length === 0 && !isLoading && (
            <div className="text-center py-8 text-muted-foreground">
              No premium earning candidates found. All policies may be fully earned or no active policies exist.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Process Modal */}
      <Modal
        isOpen={showBulkProcessModal}
        onClose={() => setShowBulkProcessModal(false)}
        title="Process Premium Earning"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Processing Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700">Selected Policies:</span>
                <span className="font-semibold ml-2">{selectedCandidates.length}</span>
              </div>
              <div>
                <span className="text-blue-700">Total Amount:</span>
                <span className="font-semibold ml-2">{formatCurrency(totalEarnable.toString(), 'EUR')}</span>
              </div>
            </div>
          </div>

          <TextInput
            label="Effective Date"
            type="date"
            value={bulkProcessDate}
            onChange={(e) => setBulkProcessDate(e.target.value)}
            required
          />

          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This will create journal entries for premium earning based on 
              the coverage periods and current date. This action cannot be undone.
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowBulkProcessModal(false)}
              disabled={processBulkEarning.isPending}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkProcess}
              disabled={processBulkEarning.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {processBulkEarning.isPending ? 'Processing...' : 'Process Premium Earning'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
